import React, { useEffect, useState } from 'react';
import FormShell from './FormShell';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { loadSavedForm, saveForm } from './formUtils';

/**
 * Generic 3-step "send-documents" / supplementary registration form for
 * services where we collect company info + contact + a list of supporting
 * documents the client will email through.
 */
const DEFAULT_DOCS = ['CIPC certificate / MOI', 'ID copy of director(s)', 'Proof of address'];

const FORM_CONFIG = {
  'restore-company': {
    title: 'Restore a Company Application',
    docs: ['CIPC deregistration notice', 'CIPC certificate (if any)', 'ID copies of directors', 'Latest annual return (if filed)'],
  },
  'paye-uif': {
    title: 'PAYE & UIF Registration',
    docs: ['CIPC certificate', 'Proof of business address', 'Bank confirmation letter', 'ID copies of directors', 'Estimated number of employees'],
  },
  'csd-registration': {
    title: 'CSD Registration',
    docs: ['CIPC certificate', 'Tax Clearance / SARS PIN', 'Bank confirmation letter', 'BEE Affidavit / Certificate', 'Proof of address'],
  },
};

const DEFAULTS = () => ({
  companyName: '',
  registrationNumber: '',
  taxNumber: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  notes: '',
});

const steps = ['Company', 'Contact & Notes', 'Review'];

const DocumentRequestForm = ({ serviceId, serviceName, onComplete }) => {
  const { toast } = useToast();
  const cfg = FORM_CONFIG[serviceId] || { title: `${serviceName} Application`, docs: DEFAULT_DOCS };
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => loadSavedForm(serviceId) || DEFAULTS());
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    saveForm(serviceId, form);
    setSaved(true);
    const t = setTimeout(() => setSaved(false), 1500);
    return () => clearTimeout(t);
  }, [serviceId, form]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    if (step === 1 && (!form.companyName || !form.registrationNumber)) {
      toast({
        title: 'Required Fields Missing',
        description: 'Company name and registration number are required.',
        variant: 'destructive',
      });
      return false;
    }
    if (step === 2 && (!form.contactName || !form.contactEmail)) {
      toast({
        title: 'Required Fields Missing',
        description: 'Contact name and email are required.',
        variant: 'destructive',
      });
      return false;
    }
    return true;
  };

  const next = () => {
    if (!validate()) return;
    setStep((s) => Math.min(s + 1, steps.length));
    window.scrollTo(0, 0);
  };
  const prev = () => {
    setStep((s) => Math.max(s - 1, 1));
    window.scrollTo(0, 0);
  };
  const submit = () => {
    if (!validate()) return;
    onComplete({ ...form, requiredDocuments: cfg.docs });
  };

  return (
    <FormShell
      title={cfg.title}
      stepLabels={steps}
      currentStep={step}
      onNext={next}
      onPrevious={prev}
      onSubmit={submit}
      autoSaved={saved}
    >
      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">Company Information</h3>
          <div>
            <Label>Registered Company Name *</Label>
            <Input value={form.companyName} onChange={(e) => set('companyName', e.target.value)} />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>CIPC Registration Number *</Label>
              <Input
                value={form.registrationNumber}
                onChange={(e) => set('registrationNumber', e.target.value)}
              />
            </div>
            <div>
              <Label>SARS Tax Number</Label>
              <Input
                value={form.taxNumber}
                onChange={(e) => set('taxNumber', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">Contact & Notes</h3>
          <div>
            <Label>Contact Person *</Label>
            <Input
              value={form.contactName}
              onChange={(e) => set('contactName', e.target.value)}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Contact Email *</Label>
              <Input
                type="email"
                value={form.contactEmail}
                onChange={(e) => set('contactEmail', e.target.value)}
              />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input
                value={form.contactPhone}
                onChange={(e) => set('contactPhone', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>Notes / Special Instructions</Label>
            <textarea
              className="mt-1 w-full p-2 border border-gray-300 rounded-md min-h-[100px]"
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
            />
          </div>

          <Card className="bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-blue-800 text-base">
                Documents you&apos;ll need to email us
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-blue-900">
              <ul className="list-disc list-inside space-y-1">
                {cfg.docs.map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
              <p className="mt-3">
                Send to <strong>hello@consultiumgroup.co.za</strong> with your order reference once
                you&apos;ve completed checkout.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {step === 3 && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-emerald-800">Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><strong>Service:</strong> {serviceName}</p>
            <p><strong>Company:</strong> {form.companyName}</p>
            <p><strong>Reg No:</strong> {form.registrationNumber}</p>
            <p><strong>Tax No:</strong> {form.taxNumber || '—'}</p>
            <p><strong>Contact:</strong> {form.contactName} — {form.contactEmail}</p>
          </CardContent>
        </Card>
      )}
    </FormShell>
  );
};

export default DocumentRequestForm;
